import React from 'react';
import * as Dates from '../utils/Dates';
import * as Time from '../utils/Time';

const ROLLING_SCALES: Time.Scale[] = [
  Dates.LAST_24_HOURS,
  Dates.LAST_7_DAYS,
  Dates.LAST_30_DAYS,
];

/**
 * Period-of-interest navigation for report-style screens at any Time.Scale.
 *
 * State: a single `timestamp` anchor inside the current period, captured at
 * mount via `Dates.now()`. Returns the start/end of that period under the
 * given scale, a formatted title, and prev/next navigation. For rolling-window
 * scales (last24Hours, last7Days, last30Days) prev/next are undefined; the
 * window is computed off the captured timestamp, so callers wanting live
 * "always now" behavior must re-mount or manually call setTimestamp.
 */
const usePeriodReport = (timezone: string, scale: Time.Scale) => {
  const [timestamp, setTimestamp] = React.useState<number>(Dates.now());
  const isRolling = ROLLING_SCALES.includes(scale);

  const start = React.useMemo(
    () => Dates.getScaleStart(timestamp, timezone, scale),
    [timestamp, timezone, scale],
  );

  const end = React.useMemo(
    () => Dates.getScaleEnd(timestamp, timezone, scale),
    [timestamp, timezone, scale],
  );

  const title = React.useMemo(
    () => Dates.formatTimestampFromScale(timestamp, timezone, scale),
    [timestamp, timezone, scale],
  );

  const canGoNext = React.useMemo(() => {
    if (isRolling) {
      return false;
    }
    return (
      Dates.differenceWithNow(
        Dates.getScaleStart(
          Dates.getNext(timestamp, timezone, scale),
          timezone,
          scale,
        ),
      ) < 0
    );
  }, [timestamp, timezone, scale, isRolling]);

  const onPrevious = React.useCallback(() => {
    if (isRolling) {
      return;
    }
    setTimestamp((prev) => Dates.getPrevious(prev, timezone, scale));
  }, [timezone, scale, isRolling]);

  const onNext = React.useCallback(() => {
    if (!canGoNext) {
      return;
    }
    setTimestamp((prev) => Dates.getNext(prev, timezone, scale));
  }, [timezone, scale, canGoNext]);

  return React.useMemo(
    () => ({
      timestamp,
      setTimestamp,
      start,
      end,
      title,
      isRolling,
      onPrevious: isRolling ? undefined : onPrevious,
      onNext: !isRolling && canGoNext ? onNext : undefined,
    }),
    [timestamp, start, end, title, isRolling, canGoNext, onPrevious, onNext],
  );
};

export default usePeriodReport;
